import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { BATCH_KIND_LABEL, detectBatchKind, parseCsv, type BatchSummary, type Destination, type Paged, type Stats, type StatusBucket } from '@ia/shared';
import { api, errorMessage } from '../api';
import { Alert, Dialog, Empty, Field, KindTag, Spinner, StatusPill, When } from '../components/ui';
import { truncate, usePolling } from '../util';

const TABS: { key: StatusBucket | 'all'; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'failed', label: 'Failed' },
  { key: 'processed', label: 'Processed' },
  { key: 'all', label: 'All' },
];
const PAGE_SIZE = 25;

export function destLabel(d: { database: string; displayName: string | null }) {
  return d.displayName ? `${d.displayName} (${d.database})` : d.database;
}

export function BatchesPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const bucket = (sp.get('bucket') ?? 'pending') as StatusBucket | 'all';
  const destinationId = sp.get('destinationId') ?? '';
  const q = sp.get('q') ?? '';
  const page = Math.max(1, Number(sp.get('page') ?? 1) || 1);

  const [search, setSearch] = useState(q);
  const [data, setData] = useState<Paged<BatchSummary> | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [dests, setDests] = useState<Destination[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!('page' in patch)) next.delete('page');
    setSp(next, { replace: true });
  };

  // Debounced search -> URL
  useEffect(() => {
    if (search === q) return;
    const t = setTimeout(() => update({ q: search.trim() || null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    api.destinations().then(setDests).catch(() => {});
  }, []);

  useEffect(() => setLoading(true), [bucket, destinationId, q, page]);

  usePolling(
    async (signal) => {
      try {
        const [b, s] = await Promise.all([
          api.batches(
            { bucket: bucket === 'all' ? undefined : bucket, destinationId: destinationId || undefined, q: q || undefined, page, pageSize: PAGE_SIZE },
            signal,
          ),
          api.stats(signal),
        ]);
        setData(b);
        setStats(s);
        setError(null);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setError(errorMessage(e));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    5000,
    true,
    [bucket, destinationId, q, page],
  );

  const count = (k: StatusBucket | 'all') => {
    if (!stats) return null;
    if (k === 'all') return Object.values(stats.buckets).reduce((a, b) => a + b, 0);
    return stats.buckets[k];
  };
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Batches</h1>
        {stats && (
          <div className="kpis" aria-label="Last 24 hours">
            <span>
              <strong>{stats.last24h.batches}</strong> batches
            </span>
            <span>
              <strong>{stats.last24h.policiesWritten}</strong> policies written
            </span>
            <span className={stats.last24h.failures ? 'text-failed' : ''}>
              <strong>{stats.last24h.failures}</strong> failures
            </span>
            <span className="muted small">last 24 h</span>
          </div>
        )}
        <div className="head-actions">
          <button type="button" className="btn btn-primary" onClick={() => setImportOpen(true)}>
            Import file
          </button>
        </div>
      </div>

      <div className="tabs" role="tablist" aria-label="Status bucket">
        {TABS.map((t) => {
          const c = count(t.key);
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={bucket === t.key}
              className={`tab tab-${t.key}`}
              onClick={() => update({ bucket: t.key === 'pending' ? null : t.key })}
            >
              {t.label}
              {c !== null && <span className="tab-count">{c}</span>}
            </button>
          );
        })}
      </div>

      <div className="toolbar">
        <label className="inline-field">
          <span>Destination</span>
          <select value={destinationId} onChange={(e) => update({ destinationId: e.target.value || null })}>
            <option value="">All destinations</option>
            {dests.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label ? `${d.label} — ${destLabel(d)}` : destLabel(d)}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-field grow">
          <span>Search</span>
          <input
            type="search"
            placeholder="Batch id, portal, portal user, Maximizer user…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <span className="muted small live" aria-live="polite">
          Auto-refresh 5 s
        </span>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Status</th>
              <th>Type</th>
              <th>Source</th>
              <th>Destination</th>
              <th className="num" title="Succeeded / failed / skipped of total">OK / Fail / Skip</th>
              <th className="num">Runs</th>
              <th>Last error</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((b) => (
              <tr
                key={b.id}
                className="clickable"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a')) return;
                  navigate(`/batches/${b.id}`);
                }}
              >
                <td>
                  <Link to={`/batches/${b.id}`} className="row-link" aria-label={`Open batch ${b.id}`}>
                    <When iso={b.createdAt} />
                  </Link>
                </td>
                <td>
                  <StatusPill status={b.status} />
                </td>
                <td>
                  <KindTag kind={b.kind} />
                </td>
                <td>
                  <div>
                    {b.portalKey} <span className="muted small">/ {b.recipeId}</span>
                  </div>
                  <div className="muted small">{b.portalUser ?? '—'}</div>
                </td>
                <td>
                  <div>{destLabel(b.destination)}</div>
                  <div className="muted small">{b.maximizerUser.displayName ?? b.maximizerUser.uid}</div>
                </td>
                <td className="num">
                  <Counts c={b.counts} />
                </td>
                <td className="num">{b.runCount}</td>
                <td className="error-cell" title={b.lastError ?? undefined}>
                  {b.lastError ? truncate(b.lastError, 80) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && !data && <Spinner />}
        {data && data.rows.length === 0 && <Empty>No batches match these filters.</Empty>}
      </div>

      {data && data.total > 0 && (
        <div className="pager">
          <span className="muted small">
            {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} of {data.total}
          </span>
          <button className="btn" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
            Previous
          </button>
          <span className="small">
            Page {page} / {totalPages}
          </span>
          <button className="btn" disabled={page >= totalPages} onClick={() => update({ page: String(page + 1) })}>
            Next
          </button>
        </div>
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={(id) => navigate(`/batches/${id}`)} />
    </div>
  );
}

type ImportFile = { name: string; content: string; headers: string[]; rows: number };

/** Dropped folders come as directory entries; walk them so every CSV inside is picked up. */
async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const entries = [...dt.items].map((i) => i.webkitGetAsEntry?.()).filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return [...dt.files];
  const out: File[] = [];
  const walk = async (e: FileSystemEntry) => {
    if (e.isFile) out.push(await new Promise<File>((res, rej) => (e as FileSystemFileEntry).file(res, rej)));
    else if (e.isDirectory) {
      const reader = (e as FileSystemDirectoryEntry).createReader();
      for (;;) {
        const chunk = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!chunk.length) break;
        for (const c of chunk) await walk(c);
      }
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

/**
 * "Import file": CSV exports (an Equisoft/connect client export, a policy file). The PAT says which
 * tenant and user it is for. Files are read here and sent as text; the type is read off the column names, with an override.
 */
function ImportDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: (batchId: string) => void }) {
  const [pat, setPat] = useState('');
  const [files, setFiles] = useState<ImportFile[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setSkipped([]);
    setPat('');
    setError(null);
  }, [open]);

  const headers = [...new Set(files.flatMap((f) => f.headers))];
  const detected = headers.length ? detectBatchKind(headers) : null;
  const rows = files.reduce((n, f) => n + f.rows, 0);

  const addFiles = async (list: File[]) => {
    const next: ImportFile[] = [];
    const skip: string[] = [];
    for (const f of list) {
      if (!/\.(csv|txt)$/i.test(f.name)) {
        skip.push(f.name);
        continue;
      }
      const content = await f.text();
      const parsed = parseCsv(content);
      next.push({ name: f.name, content, headers: parsed.headers, rows: parsed.rows.length });
    }
    setFiles((cur) => [...cur.filter((c) => !next.some((n) => n.name === c.name)), ...next]);
    setSkipped((cur) => [...new Set([...cur, ...skip])]);
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    await addFiles(await filesFromDrop(e.dataTransfer));
  };

  const submit = async () => {
    setError(null);
    if (!pat.trim()) return setError('Enter the PAT key.');
    if (!files.length) return setError('Add at least one CSV file.');
    setBusy(true);
    try {
      const b = await api.importFiles({ pat: pat.trim(), files: files.map(({ name, content }) => ({ name, content })) });
      onClose();
      onImported(b.id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="Import a file"
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy || !files.length}>
            {busy ? 'Importing…' : rows ? `Import ${rows} row${rows === 1 ? '' : 's'}` : 'Import'}
          </button>
        </>
      }
    >
      <p className="muted small">
        A CSV export, such as the client list from Equisoft/connect or a carrier's policy file, becomes a batch for the tenant the PAT belongs
        to. The worker previews it like an extension batch; then use Process or Check contacts on the batch page.
      </p>
      <Field
        label="PAT key"
        hint="The Maximizer personal access token for the tenant. When Maximizer recognises it the batch is filed under that tenant; otherwise it is taken as typed, and the batch goes to the tenant batches last came in for."
      >
        {(id) => <input id={id} type="password" autoComplete="off" value={pat} placeholder="Personal access token" onChange={(e) => setPat(e.target.value)} />}
      </Field>
      <div
        className={over ? 'dropzone over' : 'dropzone'}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => void onDrop(e)}
      >
        Drop CSV files or a folder of them here
        <div className="btn-row">
          <button type="button" className="btn btn-sm" onClick={() => fileInput.current?.click()}>
            Choose files
          </button>
          <button type="button" className="btn btn-sm" onClick={() => folderInput.current?.click()}>
            Choose folder
          </button>
        </div>
        <input ref={fileInput} type="file" accept=".csv,.txt" multiple hidden onChange={(e) => void addFiles([...(e.target.files ?? [])])} />
        <input ref={folderInput} type="file" multiple hidden {...({ webkitdirectory: '' } as Record<string, string>)} onChange={(e) => void addFiles([...(e.target.files ?? [])])} />
      </div>
      {files.length > 0 && (
        <div className="muted small">
          Columns: {headers.slice(0, 8).join(', ')}
          {headers.length > 8 ? '…' : ''}. The AI model reads them on import and files the batch as{' '}
          {detected ? `${BATCH_KIND_LABEL[detected].toLowerCase()} by the look of them` : 'insurance policies or client data'}.
        </div>
      )}
      {files.length > 0 && (
        <ul className="file-list">
          {files.map((f) => (
            <li key={f.name}>
              <span className="name">{f.name}</span>
              <span className="muted">
                {f.rows} row{f.rows === 1 ? '' : 's'} · {f.headers.length} columns
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFiles((cur) => cur.filter((c) => c.name !== f.name))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {skipped.length > 0 && (
        <div className="muted small">
          Skipped (not CSV): {skipped.join(', ')}. Excel workbooks and .tar.gz backups need to be saved as CSV first.
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Dialog>
  );
}

export function Counts({ c }: { c: BatchSummary['counts'] }) {
  return (
    <span className="counts" title={`${c.succeeded} succeeded, ${c.failed} failed, ${c.skipped} skipped of ${c.total}`}>
      <span className="text-processed">{c.succeeded}</span>
      <span className="sep">/</span>
      <span className={c.failed ? 'text-failed' : 'muted'}>{c.failed}</span>
      <span className="sep">/</span>
      <span className="muted">{c.skipped}</span>
      <span className="muted small"> of {c.total}</span>
    </span>
  );
}
